import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from "@iconify/react";
import { useTheme } from '../../contexts/ThemeContext';
import { useDirection } from '../../contexts/DirectionContext';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../router/routes';

const ComingSoonContent = () => {
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const { t } = useTranslation();
  const isDark = theme.mode === 'dark';

  return (
    <div className="h-[calc(100vh-4rem)] flex relative overflow-hidden bg-background-primary">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, var(--colors-border-primary) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
          opacity: isDark ? 0.1 : 0.08
        }} />
      </div>

      {/* Animated Gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -inset-[40%] animate-slow-spin blur-3xl ${isDark ? 'opacity-20 mix-blend-lighten' : 'opacity-30'}`}
          style={{
            background: isDark 
              ? `conic-gradient(from 0deg at 50% 50%,
                  var(--colors-primary-400) 0deg,
                  var(--colors-primary-500) 120deg,
                  var(--colors-primary-600) 240deg,
                  var(--colors-primary-400) 360deg
                )`
              : `conic-gradient(from 0deg at 50% 50%,
                  var(--colors-primary-600) 0deg,
                  var(--colors-accent-500) 100deg,
                  var(--colors-primary-600) 180deg,
                  var(--colors-primary-900) 360deg
                )`
          }}
        />
      </div>

      <div className="w-full flex items-center">
        <div className={`w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 py-12 sm:py-16 ${isRTL ? 'direction-rtl' : 'direction-ltr'}`}>
          {/* Decorative Line */}
          <div className="relative mb-8">
            <div className="relative flex justify-center">
              <span className={`px-4 text-lg font-medium tracking-wider uppercase animate-fade-in 
                ${isDark 
                  ? 'bg-[rgba(var(--colors-background-primary-rgb),0.5)] text-text-secondary' 
                  : 'bg-[rgba(var(--colors-primary-300-rgb),0.3)] text-background-primary shadow-sm'
                }`}
              >
                {t('common.comingSoon')}
              </span>
            </div>
          </div>
          
          {/* Main Content */}
          <h1 className={`mt-8 text-4xl font-bold tracking-tight sm:text-6xl animate-slide-up 
            ${isDark ? 'text-text-primary' : 'text-secondary-700'}`}
          >
            {t('common.stayTuned')}
          </h1>
          
          <p className={`mt-6 text-lg leading-8 animate-slide-up animation-delay-200 font-thin
            ${isDark ? 'text-text-secondary' : 'text-secondary-700 opacity-60'}`}
          >
            {t('common.comingSoonDescription')}
          </p>

          {/* Back to Home Link */}
          <div className="mt-10 animate-slide-up animation-delay-400">
            <Link
              to={ROUTES.HOME}
              className={`group inline-flex items-center gap-2 text-base font-medium transition-all duration-300 hover:gap-3
                ${isDark 
                  ? 'text-primary-400 hover:text-primary-300' 
                  : 'text-primary-600 hover:text-primary-700'
                }`}
            >
              <Icon 
                icon={isRTL ? "ph:arrow-right-thin" : "ph:arrow-left-thin"} 
                className={`w-5 h-5 transition-transform ${isRTL ? 'rtl:ml-1' : 'ltr:mr-1'} group-hover:translate-x-1`}
              />
              {t('common.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Higher-order component to wrap pages that aren't ready yet
export const withComingSoon = () => ComingSoonContent;

// Export the HOC as the default export
export default withComingSoon;